import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { GoogleMap, MapMarker } from '@angular/google-maps';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule } from '@ngx-translate/core';
import { EventFound } from '@smatchy/sdk';
import { environment } from '../../../../environments/environment';
import { InstagramStoryService } from '../../../services/instagram-story/instagram-story.service';

interface UsersAround {
  radiusInKm: number;
  total: number;
  sameSport: number | null;
  sportName: string | null;
}

interface EngageResult {
  targeted: number;
  invited: number;
  pushNotified: number;
  emailsSent: number;
  emailStatus: 'sent' | 'template_missing' | 'skipped';
  dryRun: boolean;
}

/**
 * The activity sheet: what it is, how it fills, and who could be engaged.
 *
 * Fill counts partner seats, never the organiser: they sit in the attendees
 * and in the capacity, so both counts drop by one. The engagement block turns
 * "nobody joined" into "N players of this sport within reach", with the
 * campaign list one click away.
 */
@Component({
  selector: 'bo-event-detail',
  templateUrl: './event-detail.component.html',
  styleUrls: ['./event-detail.component.scss'],
  imports: [
    CommonModule,
    DatePipe,
    RouterLink,
    GoogleMap,
    MapMarker,
    ButtonModule,
    RippleModule,
    ToastModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService],
})
export class EventDetailComponent implements OnChanges {
  @Input({ required: true }) event!: EventFound;

  #story = inject(InstagramStoryService);

  readonly around = signal<UsersAround | null>(null);
  readonly exporting = signal(false);
  readonly buildingStory = signal(false);

  /** The campaign composer. Every change re-previews through dryRun. */
  readonly radiusKm = signal(25);
  readonly anySport = signal(false);
  readonly levelMin = signal<number | null>(null);
  readonly levelMax = signal<number | null>(null);
  readonly channelInvitation = signal(true);
  readonly channelProximityPush = signal(false);
  readonly previewCount = signal<number | null>(null);
  readonly previewLoading = signal(false);
  readonly sendArmed = signal(false);
  readonly sending = signal(false);
  readonly campaignResult = signal<EngageResult | null>(null);
  readonly campaignError = signal(false);
  readonly radiusOptions = [5, 10, 25, 50];
  readonly levelOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  options: google.maps.MapOptions = {
    center: { lat: 46, lng: 3.5 },
    zoom: 11,
    mapTypeControl: false,
    streetViewControl: false,
    styles: [
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    ],
  };
  markerPositions: google.maps.LatLngLiteral = { lat: 0, lng: 0 };

  constructor(private messageService: MessageService, private http: HttpClient) {}

  ngOnChanges(): void {
    if (this.event?.location) {
      this.options = {
        ...this.options,
        center: { lat: this.event.location.lat, lng: this.event.location.lon },
      };
      this.markerPositions = { lat: this.event.location.lat, lng: this.event.location.lon };
      this.loadAround();
      this.resetCampaign();
      this.refreshPreview();
    }
  }

  private resetCampaign(): void {
    this.radiusKm.set(25);
    this.anySport.set(false);
    this.levelMin.set(null);
    this.levelMax.set(null);
    this.channelInvitation.set(true);
    this.channelProximityPush.set(false);
    this.previewCount.set(null);
    this.sendArmed.set(false);
    this.campaignResult.set(null);
    this.campaignError.set(false);
  }

  private campaignBody(dryRun: boolean): Record<string, unknown> {
    return {
      radiusKm: this.radiusKm(),
      anySport: this.anySport(),
      levelMin: this.levelMin() ?? undefined,
      levelMax: this.levelMax() ?? undefined,
      invitation: this.channelInvitation(),
      proximityPush: this.channelProximityPush(),
      dryRun,
    };
  }

  /** dryRun gives the exact audience: excludes, cap and filters all applied. */
  refreshPreview(): void {
    this.sendArmed.set(false);
    this.campaignResult.set(null);
    this.previewLoading.set(true);
    this.http
      .post<EngageResult>(`${environment.api}/api/v1/admin/activities/${this.event.id}/engage`, this.campaignBody(true))
      .subscribe({
        next: (res) => {
          this.previewLoading.set(false);
          this.previewCount.set(res?.targeted ?? null);
        },
        // Absent endpoint (API not redeployed yet): the composer stays hidden.
        error: () => {
          this.previewLoading.set(false);
          this.previewCount.set(null);
        },
      });
  }

  setRadius(value: number): void {
    this.radiusKm.set(value);
    this.refreshPreview();
  }

  toggleAnySport(): void {
    this.anySport.set(!this.anySport());
    this.refreshPreview();
  }

  setLevelMin(value: string): void {
    this.levelMin.set(value ? Number(value) : null);
    this.refreshPreview();
  }

  setLevelMax(value: string): void {
    this.levelMax.set(value ? Number(value) : null);
    this.refreshPreview();
  }

  toggleChannel(channel: 'invitation' | 'proximityPush'): void {
    if (channel === 'invitation') {
      this.channelInvitation.set(!this.channelInvitation());
    } else {
      this.channelProximityPush.set(!this.channelProximityPush());
    }
    this.sendArmed.set(false);
    this.campaignResult.set(null);
  }

  get canSend(): boolean {
    return (this.channelInvitation() || this.channelProximityPush()) && (this.previewCount() ?? 0) > 0;
  }

  /** Two clicks to send: the first arms and shows the exact audience on the button. */
  sendCampaign(): void {
    if (!this.canSend || this.sending()) {
      return;
    }
    if (!this.sendArmed()) {
      this.sendArmed.set(true);
      return;
    }
    this.sending.set(true);
    this.campaignError.set(false);
    this.http
      .post<EngageResult>(
        `${environment.api}/api/v1/admin/activities/${this.event.id}/engage`,
        this.campaignBody(false),
      )
      .subscribe({
        next: (res) => {
          this.sending.set(false);
          this.sendArmed.set(false);
          this.campaignResult.set(res ?? null);
        },
        error: () => {
          this.sending.set(false);
          this.sendArmed.set(false);
          this.campaignError.set(true);
        },
      });
  }

  /** Fill excludes the organiser, everywhere. */
  partners(): number {
    return Math.max((this.event?.nbAttendees ?? 0) - 1, 0);
  }

  partnerCapacity(): number {
    return Math.max((this.event?.maxNbAttendees ?? 0) - 1, 0);
  }

  fillWidth(): string {
    const capacity = this.partnerCapacity();
    return capacity ? `${Math.min((this.partners() / capacity) * 100, 100)}%` : '0%';
  }

  daysUntil(): number {
    return Math.max(Math.ceil((new Date(this.event.startDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)), 0);
  }

  urgency(): 'urgent' | 'soon' | 'later' {
    const days = this.daysUntil();
    return days <= 3 ? 'urgent' : days <= 7 ? 'soon' : 'later';
  }

  private loadAround(): void {
    this.around.set(null);
    this.http
      .get<UsersAround>(`${environment.api}/api/v1/admin/users/around`, {
        params: {
          lat: String(this.event.location.lat),
          lon: String(this.event.location.lon),
          sport: this.event.sportTitle ?? '',
        },
      })
      .subscribe({
        next: (res) => this.around.set(res ?? null),
        // The engagement block simply stays hidden when the endpoint is absent.
        error: () => this.around.set(null),
      });
  }

  /** Downloads the campaign list of same-sport members within reach. */
  exportAround(): void {
    this.exporting.set(true);
    this.http
      .get(`${environment.api}/api/v1/admin/users/around/export/csv`, {
        params: {
          lat: String(this.event.location.lat),
          lon: String(this.event.location.lon),
          sport: this.event.sportTitle ?? '',
        },
        responseType: 'blob',
      })
      .subscribe({
        next: (blob) => {
          this.exporting.set(false);
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `membres-${this.event.sportTitle ?? 'autour'}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        },
        error: () => this.exporting.set(false),
      });
  }

  /** Builds a 1080×1920 Instagram-story image of the activity and downloads it. */
  async downloadStory(): Promise<void> {
    if (this.buildingStory()) {
      return;
    }
    this.buildingStory.set(true);
    try {
      await this.#story.downloadStory(this.event);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Story',
        detail: 'La story n’a pas pu être générée.',
      });
    } finally {
      this.buildingStory.set(false);
    }
  }

  copyToClipBoard() {
    const link = 'https://smatchy.app/detail-activity/' + this.event.id;
    navigator.clipboard.writeText(link).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Lien copié dans le presse papier',
      });
    });
  }
}
